import { ReactElement, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton as CloseButton,
} from '@mui/material';
import { useBrand } from '../../context/BrandContext';
import { useLanguage } from '../../context/LanguageContext';
import { toDisplayName } from '../../utils/text';
import type { PersonIdentifier } from '../../types/api';

interface LoyaltyCardDialogProps {
  open: boolean;
  onClose: () => void;
  personData: any;
  loading?: boolean;
}

const LoyaltyCardDialog = ({ open, onClose, personData, loading = false }: LoyaltyCardDialogProps): ReactElement => {
  const { t } = useLanguage();
  const { brand } = useBrand();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [barcodeError, setBarcodeError] = useState(false);

  const now = new Date();
  const allIdentifiers = personData?.PersonResponse?.IdentifierResponse || [];
  
  const activeIdentifiers = allIdentifiers
    .filter((id: PersonIdentifier) => {
      if (id.identifierType !== 1) return false;
      
      if (id.desactivationDate) {
        const deactivationDate = new Date(id.desactivationDate);
        if (deactivationDate <= now) return false;
      }
      
      if (id.activationDate) {
        const activationDate = new Date(id.activationDate);
        if (activationDate > now) return false;
      }
      
      return true;
    })
    .sort((a: PersonIdentifier, b: PersonIdentifier) => {
      const dateA = a.activationDate ? new Date(a.activationDate).getTime() : 0;
      const dateB = b.activationDate ? new Date(b.activationDate).getTime() : 0;
      return dateB - dateA;
    });
  
  const cardIdentifier = activeIdentifiers[0]?.identifierValue || '4007007007007';
  const person = personData?.PersonResponse?.PersonResponse;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        borderBottom: '1px solid #e9ecef'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <i className="bx bx-credit-card" style={{ fontSize: 24, color: brand.primaryColor }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057' }}>
            {t('loyaltyCard.title')}
          </Typography>
        </Box>
        <CloseButton onClick={onClose} size="small" sx={{ color: '#74788d' }}>
          <i className="bx bx-x" style={{ fontSize: 24 }} />
        </CloseButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress sx={{ color: brand.primaryColor }} />
          </Box>
        ) : (
          <Box>
            {person && (
              <Box sx={{ mb: 3, mt: 3, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#495057' }}>
                  {person.firstName ? toDisplayName(person.firstName) : ''} {person.lastName ? toDisplayName(person.lastName) : ''}
                </Typography>
              </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            {cardIdentifier ? (
              <Box
                sx={{
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: '#f8f8fb',
                  borderRadius: 2,
                  border: '1px solid #e9ecef',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'white',
                    p: 1.5,
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    width: '100%',
                    minHeight: 120,
                  }}
                >
                  {barcodeError ? (
                    <Box sx={{ 
                      width: '100%', 
                      height: 120, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      background: '#f8f8fb', 
                      borderRadius: 1, 
                      border: '2px dashed #e9ecef' 
                    }}>
                      <i className="bx bx-barcode" style={{ fontSize: 48, color: '#adb5bd', marginBottom: 8 }} />
                      <Typography variant="body2" sx={{ color: '#adb5bd' }}>
                        {t('loyaltyCard.barcodeNotAvailable')}
                      </Typography>
                    </Box>
                  ) : (
                    <img
                      src={`https://ean.b1-ge.fr/barcode.php?encoding=EAN&scale=8&mode=png&IsNumberVisible=true&code=${cardIdentifier}`}
                      alt="Code-barres EAN"
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                      onError={() => setBarcodeError(true)}
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <i className="bx bx-error-circle" style={{ fontSize: 48, color: '#adb5bd' }} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  {t('loyaltyCard.noCardNumber')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LoyaltyCardDialog;
